package diana.soleil.design.iraniancanadienbazzar.feature.business

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.ReportRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ReviewRepository
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppCard
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppChip
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIcon
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.EmptyState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.LoadingState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.RatingStars
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.ReportReason
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.ReportSheet
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.ReportStatus
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.ReviewCard
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.appContentWidth
import diana.soleil.design.iraniancanadienbazzar.core.model.Review
import diana.soleil.design.iraniancanadienbazzar.core.model.formatCount
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.all_reviews_count
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.all_reviews_sort_highest
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.all_reviews_sort_lowest
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.all_reviews_sort_recent
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.all_reviews_title
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_back_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_rating_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_owner_reply
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_reviews_empty
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.report_done
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.report_failed
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.report_submit
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.review_report_action
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.review_report_reason_conflict
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.review_report_reason_fake
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.review_report_reason_offensive
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.review_report_reason_spam
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.review_report_sent
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.review_report_title
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

/** Report lifecycle for a review (mirrors the DS `ReportStatus`, with a sign-in gate for guests). */
enum class ReviewReportStatus { Idle, Sending, Sent, SignInRequired, Failed }

/** Review sort order on the All-Reviews screen. */
enum class ReviewSort { Recent, Highest, Lowest }

data class AllReviewsUiState(
    val loading: Boolean = true,
    val reviews: List<Review> = emptyList(),
    val reportStatus: ReviewReportStatus = ReviewReportStatus.Idle,
    val sort: ReviewSort = ReviewSort.Recent,
    /** Mean of every visible review's star rating (0 when none). */
    val average: Double = 0.0,
    val total: Int = 0,
    /** star (1..5) → how many reviews gave it, for the distribution bars. */
    val distribution: Map<Int, Int> = emptyMap(),
)

/** Streams every visible review for one business ("See All" from the detail screen). */
class AllReviewsViewModel(
    reviewRepository: ReviewRepository,
    private val reportRepository: ReportRepository,
    businessId: String,
) : ViewModel() {
    private val reportStatus = MutableStateFlow(ReviewReportStatus.Idle)
    private val sort = MutableStateFlow(ReviewSort.Recent)

    val state: StateFlow<AllReviewsUiState> =
        combine(reviewRepository.observeVisibleReviews(businessId), reportStatus, sort) { result, report, sortOrder ->
            val all = (result as? DataResult.Success)?.data.orEmpty()
            val sorted = when (sortOrder) {
                ReviewSort.Recent -> all.sortedByDescending { it.createdAtMillis ?: 0L }
                ReviewSort.Highest -> all.sortedByDescending { it.rating }
                ReviewSort.Lowest -> all.sortedBy { it.rating }
            }
            AllReviewsUiState(
                loading = false,
                reviews = sorted,
                reportStatus = report,
                sort = sortOrder,
                average = if (all.isEmpty()) 0.0 else all.sumOf { it.rating }.toDouble() / all.size,
                total = all.size,
                distribution = (1..5).associateWith { star -> all.count { it.rating == star } },
            )
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), AllReviewsUiState())

    fun setSort(value: ReviewSort) {
        sort.value = value
    }

    /** Files a moderation report against a review; guests trip [ReviewReportStatus.SignInRequired]. */
    fun report(reviewId: String, reason: String) {
        if (reportStatus.value == ReviewReportStatus.Sending) return
        viewModelScope.launch {
            reportStatus.value = ReviewReportStatus.Sending
            reportStatus.value = when (val result = reportRepository.fileReport("review", reviewId, reason)) {
                is DataResult.Success -> ReviewReportStatus.Sent
                is DataResult.Failure ->
                    if (result.error is AppError.Unauthorized) ReviewReportStatus.SignInRequired
                    else ReviewReportStatus.Failed
            }
        }
    }

    fun resetReport() {
        reportStatus.value = ReviewReportStatus.Idle
    }
}

@Composable
fun AllReviewsRoute(
    businessId: String,
    onBack: () -> Unit,
    modifier: Modifier = Modifier,
    onRequireSignIn: () -> Unit = {},
    viewModel: AllReviewsViewModel = koinViewModel(parameters = { parametersOf(businessId) }),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    var reportReviewId by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(state.reportStatus) {
        if (state.reportStatus == ReviewReportStatus.SignInRequired) {
            reportReviewId = null
            viewModel.resetReport()
            onRequireSignIn()
        }
    }

    reportReviewId?.let { reviewId ->
        ReportSheet(
            title = stringResource(Res.string.review_report_title),
            reasons = listOf(
                ReportReason(stringResource(Res.string.review_report_reason_fake), "fake_or_fraudulent"),
                ReportReason(stringResource(Res.string.review_report_reason_offensive), "offensive_content"),
                ReportReason(stringResource(Res.string.review_report_reason_spam), "spam"),
                ReportReason(stringResource(Res.string.review_report_reason_conflict), "conflict_of_interest"),
            ),
            status = when (state.reportStatus) {
                ReviewReportStatus.Sending -> ReportStatus.Sending
                ReviewReportStatus.Sent -> ReportStatus.Sent
                ReviewReportStatus.Failed -> ReportStatus.Failed
                ReviewReportStatus.Idle, ReviewReportStatus.SignInRequired -> ReportStatus.Idle
            },
            sentText = stringResource(Res.string.review_report_sent),
            failedText = stringResource(Res.string.report_failed),
            submitText = stringResource(Res.string.report_submit),
            doneText = stringResource(Res.string.report_done),
            onSubmit = { reason -> viewModel.report(reviewId, reason) },
            onDismiss = {
                reportReviewId = null
                viewModel.resetReport()
            },
        )
    }

    Column(modifier = modifier.fillMaxSize().background(AppTheme.colors.background)) {
        Row(
            modifier = Modifier.fillMaxWidth().statusBarsPadding().navigationBarsPadding().padding(AppTheme.spacing.lg),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
        ) {
            AppIconButton(
                iconName = "arrow_back",
                contentDescription = stringResource(Res.string.business_back_a11y),
                onClick = onBack,
                tint = AppTheme.colors.onSurface,
            )
            Text(
                text = stringResource(Res.string.all_reviews_title),
                style = AppTheme.typography.headlineLg,
                color = AppTheme.colors.onSurface,
            )
        }
        when {
            state.loading -> LoadingState()
            state.reviews.isEmpty() -> EmptyState(
                title = stringResource(Res.string.all_reviews_title),
                description = stringResource(Res.string.business_reviews_empty),
            )
            else -> LazyColumn(
                modifier = Modifier.appContentWidth(AppTheme.sizes.contentMaxWidth),
                contentPadding = PaddingValues(AppTheme.spacing.lg),
                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
            ) {
                item { ReviewSummaryHeader(state = state, onSort = viewModel::setSort) }
                items(count = state.reviews.size, key = { state.reviews[it].id }) { index ->
                    val review = state.reviews[index]
                    AppCard(modifier = Modifier.fillMaxWidth()) {
                        Row(modifier = Modifier.padding(AppTheme.spacing.lg)) {
                            Column(modifier = Modifier.weight(1f)) {
                                ReviewCard(
                                    authorName = review.authorName,
                                    rating = review.rating.toFloat(),
                                    comment = review.comment,
                                    ownerReply = review.ownerReply,
                                    ownerReplyLabel = stringResource(Res.string.business_owner_reply),
                                )
                            }
                            AppIconButton(
                                iconName = "flag",
                                contentDescription = stringResource(Res.string.review_report_action),
                                onClick = { reportReviewId = review.id },
                            )
                        }
                    }
                }
            }
        }
    }
}

/** Aggregate rating header: big average + stars + count, a 5→1 distribution, and sort chips. */
@Composable
private fun ReviewSummaryHeader(state: AllReviewsUiState, onSort: (ReviewSort) -> Unit) {
    val lang = AppTheme.language
    AppCard(modifier = Modifier.fillMaxWidth()) {
        Column(
            modifier = Modifier.padding(AppTheme.spacing.lg),
            verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
        ) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.lg),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    Text(
                        text = ratingText(state.average, lang),
                        style = AppTheme.typography.displayLg,
                        color = AppTheme.colors.onSurface,
                    )
                    RatingStars(
                        rating = state.average.toFloat(),
                        contentDescription = stringResource(
                            Res.string.business_rating_a11y,
                            ratingText(state.average, lang),
                            lang.formatCount(state.total),
                        ),
                    )
                    Text(
                        text = stringResource(Res.string.all_reviews_count, lang.formatCount(state.total)),
                        style = AppTheme.typography.labelSm,
                        color = AppTheme.colors.onSurfaceVariant,
                    )
                }
                // 5→1 distribution bars.
                Column(
                    modifier = Modifier.weight(1f),
                    verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xxs),
                ) {
                    for (star in 5 downTo 1) {
                        val count = state.distribution[star] ?: 0
                        val fraction = if (state.total == 0) 0f else count.toFloat() / state.total
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
                        ) {
                            Text(
                                text = lang.formatCount(star),
                                style = AppTheme.typography.labelSm,
                                color = AppTheme.colors.onSurfaceVariant,
                            )
                            AppIcon(name = "star", size = AppTheme.sizes.iconSm, tint = AppTheme.colors.secondary, filled = true)
                            Box(
                                modifier = Modifier
                                    .weight(1f)
                                    .height(AppTheme.sizes.skeletonLine)
                                    .clip(AppTheme.shapes.pill)
                                    .background(AppTheme.colors.surfaceContainer),
                            ) {
                                Box(
                                    modifier = Modifier
                                        .fillMaxHeight()
                                        .fillMaxWidth(fraction)
                                        .clip(AppTheme.shapes.pill)
                                        .background(AppTheme.colors.secondary),
                                )
                            }
                        }
                    }
                }
            }
            Row(
                modifier = Modifier.horizontalScroll(rememberScrollState()),
                horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
            ) {
                ReviewSortChip(ReviewSort.Recent, stringResource(Res.string.all_reviews_sort_recent), state.sort, onSort)
                ReviewSortChip(ReviewSort.Highest, stringResource(Res.string.all_reviews_sort_highest), state.sort, onSort)
                ReviewSortChip(ReviewSort.Lowest, stringResource(Res.string.all_reviews_sort_lowest), state.sort, onSort)
            }
        }
    }
}

@Composable
private fun ReviewSortChip(value: ReviewSort, label: String, selected: ReviewSort, onSort: (ReviewSort) -> Unit) {
    AppChip(label = label, selected = value == selected, onClick = { onSort(value) })
}
